/*
  Boops client-observability beacon — the deferred runtime.

  This file is the BROWSER half of the client-observability system. It was
  inline in snippets/observability.liquid until the 2026-09-14 Ruling M asset
  refactor; the body below is that snippet's script, unchanged, so the contract
  in data-warehouse/docs/client_observability/beacon_schema.md still governs
  every field name.

  WHY IT IS AN ASSET
    An asset is cached across pages and loads with `defer`, so it costs the
    first card nothing. The snippet keeps only a queue stub: it arms the three
    page-level error listeners and the three bridge functions at head-parse
    time, pushes onto window.__cwObsvQ, and this file drains that queue and
    takes over. There is ONE code path for ordinary PDPs and for V2.

  DEBUG STREAM (IAB-plan W5a)
    The console WRAPPER — the part that costs a shopper — stays compile-time
    gated inside snippets/observability.liquid under
    {%- if settings.debug_mode_enabled -%}. A normal shopper's page still
    contains no console reassignment at all. Only the buffered transport lives
    here, and it is inert until that gated wrapper pushes a "consoleDebug"
    entry onto the queue.

  ORIGINAL MODULE DOCUMENTATION (carried over verbatim from the snippet;
  the two stage descriptions and the defensive posture are unchanged):

  Client-side observability beacon (Boops Pets) — the BROWSER half of the
  client-observability system. Component C1 of the Phase 1-4 build.

  CONTRACT (single source of truth for every field name across all five layers —
  client → edge → raw BQ → dbt staging → dbt marts):
    data-warehouse/docs/client_observability/beacon_schema.md
  PLAN: docs/plans/boops-client-observability-logging-2026-06-22.md (§6.1 shape).

  WHAT THIS DOES (two stages, value-phased):
    STAGE 1 (Phase 1, ESSENTIAL — errors 100%, NOT analytics-gated):
      - window error / unhandledrejection / capture-phase resource-error hooks
        (addEventListener, NEVER window.onerror= — preserves Microsoft Clarity's hook)
      - a fetch() wrapper that emits network_failure (path-only, no PII)
      - the native-/cart-fallthrough COUNTER: the measurement instrument for the
        confirmed ATC tap-before-upgrade race
        (docs/research/boops-inapp-atc-upgrade-race-rootcause-validation-2026-06-22.md).
        This is MEASUREMENT ONLY — it never preventDefaults, never blocks a submit;
        the render-time CSS fix is a separate, physical-device-gated workstream.
    STAGE 2 (Phase 2, ANALYTICS — sampled, gated on consent, DEFERRED after LCP):
      - a compact inline RUM module (LCP / CLS / INP / FCP / TTFB via buffered
        PerformanceObserver) emitting web_vitals events
      - custom_timing events (time_to_add_to_cart_*, predictive_search_latency,
        drawer_open_*).
      The official google/web-vitals attribution build can be VENDORED later for
      richer LoAF attribution; the inline implementation here is the deliverable
      and stays within the ~4 KB gzip budget (no external CDN fetch on the
      critical path).

  DEFENSIVE POSTURE (mirrors snippets/openai-pixel.liquid):
    The whole module is one try/catch IIFE — an observability failure must NEVER
    break the storefront. Every handler is itself wrapped. Config is read LAZILY
    at runtime (NOT at parse time) because theme.liquid renders this snippet at
    :14, BEFORE js-variables.liquid populates window.themeVariables at :59.

  TRANSPORT:
    Events batch into BUF and flush as ONE navigator.sendBeacon (text/plain Blob —
    a safelisted content-type, so NO CORS preflight; sendBeacon cannot preflight)
    on visibilitychange->hidden + pagehide; fetch({keepalive:true}) fallback.
    Re-armed on bfcache pageshow. Envelope = { v, brand, token, events } (§1).

  CONSENT (§5/§8):
    Shopify.loadFeatures(['consent-tracking-api']) first, then gate ANALYTICS-class
    events (web_vitals / custom_timing) on
    granted -> analytics_granted, explicit analytics "no" -> no RUM, otherwise
    pending (API loaded) or unknown (API absent), per R4. ESSENTIAL events
    (js_error / unhandled_rejection / resource_error / network_failure /
    native_cart_submit_observed / cart_add_error / cart_change_error /
    free_shipping_alert / free_shipping_release) are NOT analytics-gated. RUM leaves at page hide even without an answer;
    explicit-decline RUM is discarded. Re-armed on visitorConsentCollected /
    trackingConsentAccepted.

  CLIENT SCRUB (defense-in-depth; edge re-scrubs as the canonical boundary, §5):
    query/hash stripped from page_path/filename/resource_url/net_path; referrer
    host-only; breadcrumb clicks as CSS selectors (never text); never form values,
    emails, phones, tokens, or full IPs.

  DEBUG STREAM (IAB-plan W5a — OPERATOR-ONLY, COMPILE-TIME INERT for shoppers):
    A second, richer event stream gated behind the Liquid setting
    {%- if settings.debug_mode_enabled -%}. When the theme setting is OFF (the
    default, and the ONLY value live traffic ever sees) the entire block below is
    NOT EMITTED by Liquid — there is zero JS, zero buffer, zero console wrapper,
    zero cost for a normal shopper. This is a COMPILE-TIME gate, never a runtime
    boolean: a normal page literally does not contain the code.
    When an operator flips the setting ON for a Track-B in-app test session it adds:
      - a console wrapper that BUFFERS console.log/warn/error (with light metadata
        + the existing source_class classify()) — it never synchronously
        re-stringifies/routes every call; the heavy work is deferred to the flush.
      - a MICRO-BATCHED transport: events append to DEBUG_BUF and flush on a ~100ms
        debounce OR when the buffer nears ~32 KB (a safe margin under the 64 KB iOS
        WKWebView fetch({keepalive:true}) body quota — WebKit bug 168865), via
        fetch({keepalive:true}) ONLY (sendBeacon shares the same per-origin quota
        and we never want a console flood to evict the Stage-1 page-hide beacon).
      - the envelope carries is_debug_stream:true so the edge can route the debug
        traffic to a separate, throwaway sink and never mix it with the canonical
        clientlog table.
    The Stage-1 error/RUM handlers above stay OUTSIDE this gate, unchanged — the
    debug stream is purely additive and the production beacon is the source of truth.
*/
  (function () {
    try {
      var SCHEMA_VERSION = 1;
      // Set by the debug transport block at the end of this file. The console
      // wrapper itself stays compile-time gated in the snippet; this asset only
      // holds the buffered transport it forwards into.
      var debugConsoleRecord = null;
      var FINGERPRINT_VERSION = 1;
      var BREADCRUMB_CAP = 20;
      var MAX_PER_SESSION = 20;     // REAL-error flood guard (js_error / unhandled_rejection / resource_error)
      var MAX_CART_FT_PER_SESSION = 8;   // native_cart_submit_observed budget (fires on EVERY ATC submit)
      var MAX_NET_PER_SESSION = 30;      // network_failure budget (own counter)
      var MAX_EVENTS_PER_BATCH = 64;

      var BUF = [];                 // pending events (essential + buffered analytics)
      // Per-category budgets kept SEPARATE so a noisy-but-healthy session can never evict real
      // errors: the cart-fallthrough counter fires on every add-to-cart submit and the fetch
      // wrapper on every 5xx/throw, so if they shared ERROR_COUNT an ATC-heavy or flaky-network
      // session would exhaust the budget and silently drop the js_error/rejection/resource_error
      // we actually care about. ERROR_COUNT is now reserved for those real errors ONLY.
      var ERROR_COUNT = 0;          // counts toward MAX_PER_SESSION (real errors only)
      var CART_FT_COUNT = 0;        // counts toward MAX_CART_FT_PER_SESSION
      var NET_COUNT = 0;            // counts toward MAX_NET_PER_SESSION
      var BREADCRUMBS = [];         // ring buffer
      var SESSION_ID = "";          // hashed at the edge; raw here
      var SENT_RUM = false;         // RUM sampling decision applied once per load
      var RUM_SAMPLED_IN = false;   // whether this load is in the RUM sample
      var rumLcpValue = null;       // last LCP candidate, flushed at hidden

      // ---- lazy config read (themeVariables may not exist at parse time) ----
      function cfg() {
        try {
          var tv = window.themeVariables || {};
          var obs = tv.observability || {};
          return {
            endpoint: obs.endpoint || "",
            brand: obs.brand || "Boops Pets",
            token: obs.token || "",
            sampleRumPct: (typeof obs.sampleRumPct === "number") ? obs.sampleRumPct : 0,
            enableRum: obs.enableRum === true,
            themeReleaseId: stringify(tv.themeReleaseId),
            themeAssetVersion: stringify(tv.themeAssetVersion),
            pageType: (tv.settings && stringify(tv.settings.pageType)) || "",
            // cartType drives the derived native-cart race classifier (contract §3.6).
            // Boops' theme setting (settings.cart_type) can be drawer|message|page, but Boops
            // is PINNED to 'drawer' — the only value the race applies to ('page' = by-design
            // success redirect, counted separately; 'message' is out of scope for the classifier).
            cartType: (tv.settings && stringify(tv.settings.cartType)) || "unknown"
          };
        } catch (e) {
          return { endpoint: "", brand: "Boops Pets", token: "", sampleRumPct: 0,
                   enableRum: false, themeReleaseId: "", themeAssetVersion: "",
                   pageType: "", cartType: "unknown" };
        }
      }

      function stringify(v) {
        if (v === null || v === undefined) return "";
        return (typeof v === "string") ? v : String(v);
      }

      // ---- small utils ----
      function nowMs() {
        try { return Date.now(); } catch (e) { return 0; }
      }

      function rand() {
        try { return Math.random(); } catch (e) { return 0; }
      }

      function genId() {
        return nowMs().toString(36) + "-" + rand().toString(36).slice(2, 10);
      }

      // localStorage probe — sets storage_blocked + seeds a stable session id.
      var STORAGE_BLOCKED = false;
      (function probeStorage() {
        try {
          var k = "__cw_obsv_probe__";
          window.localStorage.setItem(k, "1");
          window.localStorage.removeItem(k);
        } catch (e) {
          STORAGE_BLOCKED = true;
        }
        try {
          SESSION_ID = (window.__cwDw && window.__cwDw.s) || "";
          SESSION_ID = window.sessionStorage.getItem("cw_obsv_sid") || SESSION_ID;
          if (!SESSION_ID) {
            SESSION_ID = genId();
            window.sessionStorage.setItem("cw_obsv_sid", SESSION_ID);
          }
        } catch (e) {
          if (!SESSION_ID) SESSION_ID = (window.__cwDw && window.__cwDw.s) || genId();
        }
      })();

      // ---- URL scrub: path only, no query/hash. NEVER throws. ----
      function pathOnly(value) {
        if (!value) return "";
        try {
          // Absolute or protocol-relative -> parse and keep pathname.
          if (/^https?:\/\//i.test(value) || value.indexOf("//") === 0) {
            var u = new URL(value, location.href);
            return u.pathname || "";
          }
          // Relative path/string -> strip ?query and #hash manually.
          var s = String(value);
          var qi = s.indexOf("?"); if (qi !== -1) s = s.slice(0, qi);
          var hi = s.indexOf("#"); if (hi !== -1) s = s.slice(0, hi);
          return s;
        } catch (e) {
          var f = String(value);
          var q = f.indexOf("?"); if (q !== -1) f = f.slice(0, q);
          var h = f.indexOf("#"); if (h !== -1) f = f.slice(0, h);
          return f;
        }
      }

      function referrerHost() {
        try {
          if (!document.referrer) return "";
          return new URL(document.referrer).host || "";
        } catch (e) { return ""; }
      }

      // source_class classification (contract §2): first_party (same-origin / theme
      // CDN) | third_party (known vendor hosts) | inapp_injected (webview symbols) | unknown
      var THIRD_PARTY_HINTS = [
        "clarity.ms", "tolstoy", "gotolstoy", "klaviyo", "rechargeapps", "recharge",
        "onyou", "openai.com", "bzrcdn.openai.com", "googletagmanager", "google-analytics",
        "googleadservices", "facebook.net", "connect.facebook", "tiktok", "ecomposer",
        "yotpo", "judge.me", "doubleclick", "snapchat", "pinterest", "shopifycloud",
        "monorail", "hotjar", "segment"
      ];
      var INAPP_INJECTED_HINTS = [
        "_AutofillCallbackHandler", "gCrWeb", "webkit.messageHandlers"
      ];
      function classify(filename) {
        try {
          var f = String(filename || "");
          if (!f) return "unknown";
          for (var i = 0; i < INAPP_INJECTED_HINTS.length; i++) {
            if (f.indexOf(INAPP_INJECTED_HINTS[i]) !== -1) return "inapp_injected";
          }
          // A5: Shopify-injected platform scripts under /cdn/shopifycloud/* (perf-kit collector,
          // consent-tracking-api, etc.) are served from the shop CDN but are NOT Boops code —
          // classify by path so they don't land in the first-party triage queue (audit §4.4).
          if (f.indexOf("/cdn/shopifycloud/") !== -1) return "third_party";
          var host = "";
          try { host = new URL(f, location.href).host; } catch (e) { host = ""; }
          if (host) {
            if (host === location.host) return "first_party";
            if (host.indexOf("shopifycdn.com") !== -1 || host.indexOf("cdn.shopify.com") !== -1) {
              return "first_party"; // theme assets served from Shopify CDN
            }
            for (var j = 0; j < THIRD_PARTY_HINTS.length; j++) {
              if (host.indexOf(THIRD_PARTY_HINTS[j]) !== -1) return "third_party";
            }
            return "unknown";
          }
          return "unknown";
        } catch (e) { return "unknown"; }
      }

      // ---- coarse UA buckets + in-app detection ----
      var UA = (function () { try { return navigator.userAgent || ""; } catch (e) { return ""; } })();
      function isInAppBrowser() {
        // FBAN/FBAV = Facebook iOS; Instagram = IG (iOS + Android); FB_IAB/FB4A = Facebook Android
        // in-app browser; \bLine\/ = Line. FB-Android is the only miss without FB_IAB/FB4A — without
        // them those sessions mislabel is_inapp_browser=false and undercount every downstream IAB cut
        // (IAB-plan W1). Match on UA *tokens*, never version numbers.
        return /FBAN|FBAV|FB_IAB|FB4A|Instagram|\bLine\//i.test(UA);
      }
      function browserBucket() {
        if (/Edg\//.test(UA)) return "edge";
        if (/CriOS|Chrome\//.test(UA)) return "chrome";
        if (/FxiOS|Firefox\//.test(UA)) return "firefox";
        if (/Version\/.*Safari/.test(UA) || /Safari/.test(UA)) return "safari";
        return "other";
      }
      function osBucket() {
        if (/iPhone|iPad|iPod|iOS/.test(UA)) return "ios";
        if (/Android/.test(UA)) return "android";
        if (/Mac OS X|Macintosh/.test(UA)) return "macos";
        if (/Windows/.test(UA)) return "windows";
        if (/Linux/.test(UA)) return "linux";
        return "other";
      }
      function deviceClass() {
        try {
          var w = window.innerWidth || 0;
          if (/iPad|Tablet/.test(UA) || (/Android/.test(UA) && !/Mobile/.test(UA))) return "tablet";
          if (/Mobi|iPhone|iPod|Android.*Mobile/.test(UA)) return "mobile";
          if (w && w < 768) return "mobile";
          if (w && w < 1024) return "tablet";
          return "desktop";
        } catch (e) { return "desktop"; }
      }

      function effectiveConnectionType() {
        try { return (navigator.connection && navigator.connection.effectiveType) || ""; }
        catch (e) { return ""; }
      }
      function deviceMemory() {
        try {
          var m = navigator.deviceMemory;
          return (typeof m === "number") ? m : null;
        } catch (e) { return null; }
      }
      function navigationType() {
        try {
          var nav = performance.getEntriesByType("navigation");
          return (nav && nav[0] && nav[0].type) || "";
        } catch (e) { return ""; }
      }

      // ---- consent (§8) ----
      function customerPrivacy() {
        try { return window.Shopify && window.Shopify.customerPrivacy; } catch (e) { return null; }
      }
      function analyticsOK() {
        try {
          var cp = customerPrivacy();
          return !!(cp && cp.analyticsProcessingAllowed && cp.analyticsProcessingAllowed() === true);
        } catch (e) { return false; }
      }
      function consentState() {
        // R4: a missing answer is pending, not a decline. Match the head probe.
        var cp = customerPrivacy();
        if (!cp) return "unknown";
        if (analyticsOK()) return "analytics_granted";
        try {
          var consent = typeof cp.currentVisitorConsent === "function" ? cp.currentVisitorConsent() : null;
          if (consent && consent.analytics === "no") return "essential_only";
        } catch (e) {}
        return "pending";
      }
      // ESSENTIAL event types are sent regardless of analytics consent.
      // NOTE: the contract (beacon_schema.md §3.1/§5) also defines a console_error ANALYTICS
      // type, but this client emits NO console.error wrapper, so console_error is intentionally
      // absent here — keep this enum to event types we actually record. Add it back only when a
      // capped, analytics-gated console.error wrapper ships.
      var ANALYTICS_EVENT_TYPES = { web_vitals: 1, custom_timing: 1 };
      function kindOf(ev) {
        return ANALYTICS_EVENT_TYPES[ev.event_type] ? "analytics" : "essential";
      }

      if (window.Shopify && window.Shopify.loadFeatures) {
        try {
          window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function () {
            rearm();
          });
        } catch (e) {}
      }
      function rearm() {
        try { flush(); } catch (e) {}
      }
      try {
        document.addEventListener("visitorConsentCollected", rearm); // current event
        document.addEventListener("trackingConsentAccepted", rearm); // legacy/back-compat
      } catch (e) {}

      // ---- breadcrumbs (ring buffer, ≤20; target is a CSS selector/path, never text) ----
      function pushBreadcrumb(type, target) {
        try {
          BREADCRUMBS.push({ ts: nowMs(), type: String(type), target: String(target || "") });
          if (BREADCRUMBS.length > BREADCRUMB_CAP) BREADCRUMBS.shift();
        } catch (e) {}
      }
      function cssSelector(el) {
        try {
          if (!el || el.nodeType !== 1) return "";
          var parts = [];
          var node = el;
          var depth = 0;
          while (node && node.nodeType === 1 && depth < 4) {
            var sel = node.nodeName.toLowerCase();
            if (node.id) { sel += "#" + node.id; parts.unshift(sel); break; }
            if (node.className && typeof node.className === "string") {
              var cls = node.className.trim().split(/\s+/).slice(0, 2).join(".");
              if (cls) sel += "." + cls;
            }
            parts.unshift(sel);
            node = node.parentNode;
            depth++;
          }
          return parts.join(" > ");
        } catch (e) { return ""; }
      }
      try {
        document.addEventListener("click", function (e) {
          try { pushBreadcrumb("click", cssSelector(e.target)); } catch (err) {}
        }, true);
      } catch (e) {}
      // navigation breadcrumb (path only, no query/hash)
      pushBreadcrumb("nav", pathOnly(location.pathname));

      // ---- message / stack scrub (client allowlist; edge re-normalizes canonically, §4) ----
      function scrubText(value) {
        if (!value) return "";
        try {
          var s = String(value);
          s = s.replace(/https?:\/\/[^\s)'"]+/gi, "<url>");
          s = s.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>");
          s = s.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, "<pii>");
          s = s.replace(/\b\+?\d[\d\-().\s]{7,}\d\b/g, "<pii>");
          s = s.replace(/\b[0-9a-f]{8,}\b/gi, "<hex>");
          if (s.length > 2000) s = s.slice(0, 2000);
          return s;
        } catch (e) { return ""; }
      }

      // ---- client-side fingerprint (advisory; edge re-normalizes canonically, §4) ----
      // sha1 is not available without a crypto dep; use a cheap stable 32-bit FNV-1a
      // hash of (error_name + normalized message/top-frames). The edge re-computes
      // the canonical sha1 fingerprint, so this is grouping-advisory only.
      function fnv1a(str) {
        try {
          var h = 0x811c9dc5;
          for (var i = 0; i < str.length; i++) {
            h ^= str.charCodeAt(i);
            h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
          }
          return ("00000000" + h.toString(16)).slice(-8);
        } catch (e) { return "00000000"; }
      }
      function normalizeForFingerprint(text) {
        if (!text) return "";
        try {
          var s = String(text);
          s = s.replace(/https?:\/\/[^\s)'"]+/gi, "<url>");
          s = s.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "<uuid>");
          s = s.replace(/\b[0-9a-f]{8,}\b/gi, "<hex>");
          s = s.replace(/\b\d+(\.\d+)?\b/g, "<num>");
          s = s.replace(/["'][^"']*["']/g, "<str>");
          s = s.replace(/\s+/g, " ").trim().toLowerCase();
          return s;
        } catch (e) { return ""; }
      }
      function computeFingerprint(errorName, message, stack) {
        try {
          var name = errorName || "";
          var stackKey = "";
          if (stack) {
            // keep top first_party frames, strip query/hash from filenames.
            var lines = String(stack).split("\n").slice(0, 6);
            var kept = [];
            for (var i = 0; i < lines.length && kept.length < 3; i++) {
              var line = lines[i];
              var m = line.match(/(https?:\/\/[^\s):]+|\/[^\s):]+)/);
              if (m && classify(m[1]) === "first_party") {
                kept.push(pathOnly(m[1]) + ":" + (line.match(/:(\d+):/) ? line.match(/:(\d+):/)[1] : ""));
              }
            }
            stackKey = kept.join("|");
          }
          var basis = stackKey ? (name + "|" + stackKey) : (name + "|" + normalizeForFingerprint(message));
          return fnv1a(basis);
        } catch (e) { return fnv1a(String(errorName || "")); }
      }

      // ---- common-field envelope per event (contract §2) ----
      function baseEvent(eventType) {
        var c = cfg();
        return {
          event_id: genId(),
          event_type: eventType,
          session_id: SESSION_ID,
          client_ts: nowMs(),
          page_type: c.pageType,
          page_path: pathOnly(location.pathname),
          referrer_host: referrerHost(),
          theme_release_id: c.themeReleaseId,
          theme_asset_version: c.themeAssetVersion,
          source_class: "first_party",
          is_inapp_browser: isInAppBrowser(),
          browser: browserBucket(),
          os: osBucket(),
          device_class: deviceClass(),
          viewport_w: (window.innerWidth | 0),
          viewport_h: (window.innerHeight | 0),
          effective_connection_type: effectiveConnectionType(),
          device_memory: deviceMemory(),
          navigation_type: navigationType(),
          storage_blocked: STORAGE_BLOCKED,
          consent_state: consentState(),
          sample_rate: 1.0,
          breadcrumbs: BREADCRUMBS.slice(-BREADCRUMB_CAP)
        };
      }

      function record(ev) {
        try {
          if (BUF.length >= 256) return; // hard memory ceiling
          BUF.push(ev);
        } catch (e) {}
      }

      // STAGE 1 — ESSENTIAL listeners (passive; ~0ms until an event fires)
      function onError(e) {
        try {
          if (ERROR_COUNT >= MAX_PER_SESSION) return;
          // Resource-load errors surface here too in capture phase; those are
          // handled by onResourceError. A real script error has e.error/message.
          if (!e || (!e.message && !e.error)) return;
          ERROR_COUNT++;
          var err = e.error || {};
          var ev = baseEvent("js_error");
          ev.error_name = (err && err.name) || "Error";
          ev.message = scrubText(e.message || (err && err.message) || "");
          ev.filename = pathOnly(e.filename || "");
          ev.line = (e.lineno | 0);
          ev.col = (e.colno | 0);
          ev.stack = scrubText((err && err.stack) || "");
          ev.source_class = classify(e.filename || "");
          ev.fingerprint = computeFingerprint(ev.error_name, ev.message, (err && err.stack) || "");
          ev.fingerprint_version = FINGERPRINT_VERSION;
          record(ev);
        } catch (err) {}
      }

      function onRejection(e) {
        try {
          if (ERROR_COUNT >= MAX_PER_SESSION) return;
          ERROR_COUNT++;
          var reason = e && e.reason;
          var name = "Error", message = "", stack = "";
          if (reason && typeof reason === "object") {
            name = reason.name || "Error";
            message = reason.message || "";
            stack = reason.stack || "";
          } else {
            message = String(reason);
          }
          var ev = baseEvent("unhandled_rejection");
          ev.error_name = name;
          ev.message = scrubText(message);
          ev.filename = "";
          ev.line = 0;
          ev.col = 0;
          ev.stack = scrubText(stack);
          ev.source_class = classify(stack);
          ev.fingerprint = computeFingerprint(ev.error_name, ev.message, stack);
          ev.fingerprint_version = FINGERPRINT_VERSION;
          record(ev);
        } catch (err) {}
      }

      function onResourceError(e) {
        try {
          if (ERROR_COUNT >= MAX_PER_SESSION) return;
          var t = e && e.target;
          if (!t || t === window || !t.tagName) return; // a script error, not a resource
          var url = t.src || t.href || "";
          if (!url) return;
          ERROR_COUNT++;
          var tag = t.tagName.toLowerCase();
          var initiator = "other";
          if (tag === "img") initiator = "img";
          else if (tag === "script") initiator = "script";
          else if (tag === "link") initiator = (t.rel === "stylesheet") ? "css" : "link";
          else if (tag === "source") initiator = "img";
          var ev = baseEvent("resource_error");
          ev.error_name = "";
          ev.resource_url = pathOnly(url);
          ev.resource_initiator_type = initiator;
          ev.source_class = classify(url);
          ev.fingerprint = computeFingerprint("resource_error", ev.resource_url, "");
          ev.fingerprint_version = FINGERPRINT_VERSION;
          record(ev);
        } catch (err) {}
      }

      // The inline stub in snippets/observability.liquid owns the three
      // page-level listeners. They are armed at head-parse time, long before
      // this deferred asset runs, and they forward through __cwObsvQ. Arming
      // them a second time here would double-count every error.

      // ---- fetch wrapper: network_failure (path only, no PII) ----
      (function wrapFetch() {
        try {
          if (typeof window.fetch !== "function" || window.__cwObsvFetchWrapped) return;
          var orig = window.fetch;
          window.__cwObsvFetchWrapped = true;
          window.fetch = function (input, init) {
            var startedAt = nowMs();
            var method = "GET", urlStr = "";
            try {
              if (init && init.method) method = String(init.method).toUpperCase();
              else if (input && input.method) method = String(input.method).toUpperCase();
              urlStr = (typeof input === "string") ? input : (input && input.url) || "";
            } catch (e) {}
            return orig.apply(this, arguments).then(function (resp) {
              try {
                if (resp && resp.status >= 500) {
                  emitNetworkFailure(method, urlStr, resp.status, nowMs() - startedAt, true);
                }
              } catch (e) {}
              return resp;
            }, function (err) {
              try { emitNetworkFailure(method, urlStr, 0, nowMs() - startedAt, true); } catch (e) {}
              throw err;
            });
          };
        } catch (e) {}
      })();

      function emitNetworkFailure(method, url, status, durationMs, failed) {
        try {
          if (NET_COUNT >= MAX_NET_PER_SESSION) return;  // own budget — never gated on ERROR_COUNT
          NET_COUNT++;
          var ev = baseEvent("network_failure");
          ev.error_name = "";
          ev.net_method = method || "GET";
          ev.net_path = pathOnly(url);
          ev.net_status = (status | 0);
          ev.net_duration_ms = (typeof durationMs === "number") ? durationMs : 0;
          ev.net_failed = !!failed;
          ev.source_class = classify(url);
          ev.fingerprint = computeFingerprint("network_failure", ev.net_method + " " + ev.net_path + " " + ev.net_status, "");
          ev.fingerprint_version = FINGERPRINT_VERSION;
          record(ev);
        } catch (e) {}
      }

      // NATIVE-/CART-FALLTHROUGH COUNTER (ESSENTIAL; contract §3.6) — the ATC
      // tap-before-upgrade race MEASUREMENT instrument. Never preventDefaults /
      // stopImmediatePropagation (coexists with openai-pixel's capture listener).
      // CRITICAL TIMING: capture fires BEFORE product-form's bubble preventDefault,
      // so a sync defaultPrevented read false-positives every healthy add — defer it
      // to setTimeout(0). True (uncaught) race = default_prevented:false + upgraded:false +
      // cart_type:'drawer'; cart_type:'page' = by-design redirect (counted separately).
      // POST-W8: once snippets/atc-upgrade-guard.liquid (the IAB-plan W8 fix) ships live,
      // a CAUGHT race flips to default_prevented:TRUE + upgraded:false (the guard cancels
      // the native submit). The mart classifies that as native_cart_race_caught, so the
      // measure-first before/after stays legible (uncaught -> ~0, caught -> prior rate).
      function inferFormSurface(form) {
        try {
          if (!form) return "unknown";
          var cls = (form.className && typeof form.className === "string") ? form.className : "";
          var id = form.id || "";
          // EComposer bare-form quickview: class ecom-product-form / id product_form_..._ecom-...
          if (/ecom-product-form/.test(cls) || /_ecom-/.test(id)) return "quickview";
          if (form.closest) {
            if (form.closest("product-sticky-bar, .product-sticky-bar")) return "sticky";
            if (form.closest("product-card, .product-card")) return "product_card";
            if (form.closest("dialog, [role='dialog'], .modal, quick-buy, .quick-buy")) return "quickview";
            if (form.closest("product-form")) return "pdp";
          }
          return "unknown";
        } catch (e) { return "unknown"; }
      }
      try {
        document.addEventListener("submit", function (e) {
          try {
            var form = e && e.target;
            if (!form || !form.action || form.action.indexOf("/cart/add") === -1) return;
            var c = cfg();
            // Resolve the surface + upgrade state synchronously (at submit time)...
            var surface = inferFormSurface(form);
            var upgraded = false;
            try {
              upgraded = !!(window.customElements && window.customElements.get("product-form") &&
                form.closest && form.closest("product-form") &&
                form.closest("product-form").matches &&
                form.closest("product-form").matches(":defined"));
            } catch (uerr) { upgraded = false; }
            // ...but DEFER the defaultPrevented read past the bubble phase + microtask,
            // else every healthy add false-positives (capture fires first).
            setTimeout(function () {
              try {
                if (CART_FT_COUNT >= MAX_CART_FT_PER_SESSION) return;  // own budget — never gated on ERROR_COUNT
                CART_FT_COUNT++;
                var ev = baseEvent("native_cart_submit_observed");
                ev.error_name = "";
                ev.default_prevented = !!e.defaultPrevented;
                ev.product_form_upgraded = upgraded;
                ev.form_surface = surface;
                ev.cart_type = c.cartType || "unknown";
                record(ev);
              } catch (terr) {}
            }, 0);
          } catch (serr) {}
        }, true); // capture phase — fires BEFORE product-form's bubble preventDefault
      } catch (e) {}

      // Ruling I: essential purchase diagnostics use the existing clientlog rail.
      // Each event has its own storage key: an acknowledgement in one tab cannot
      // overwrite a concurrent tab's event. Only persisted event IDs remove it.
      var PURCHASE_PREFIX = "cw_obsv_purchase_";
      var PURCHASE_CAP = 20;
      var PURCHASE_TTL_MS = 86400000;
      var purchaseCount = 0;
      var purchaseSending = false;
      var purchaseAttempts = 0;
      var purchaseTimer = null;
      var pendingPurchases = [];
      function purchaseOutbox() {
        var entries = [];
        if (STORAGE_BLOCKED) return entries;
        try {
          var keys = [];
          for (var i = 0; i < localStorage.length; i++) {
            var key = localStorage.key(i);
            if (key && key.indexOf(PURCHASE_PREFIX) === 0) keys.push(key);
          }
          keys.forEach(function (key) {
            try {
              var event = JSON.parse(localStorage.getItem(key));
              if (!event || event.event_type !== "buy_box_purchase" ||
                  typeof event.client_ts !== "number" ||
                  event.client_ts < nowMs() - PURCHASE_TTL_MS || event.client_ts > nowMs() + 60000) {
                localStorage.removeItem(key);
              } else entries.push({ key: key, event: event });
            } catch (err) { localStorage.removeItem(key); }
          });
          entries.sort(function (a, b) { return b.event.client_ts - a.event.client_ts; });
          entries.slice(PURCHASE_CAP).forEach(function (entry) { localStorage.removeItem(entry.key); });
          return entries.slice(0, PURCHASE_CAP);
        } catch (err) { return entries; }
      }
      function schedulePurchaseFlush(delay, resetAttempts) {
        if (purchaseTimer !== null) return;
        purchaseTimer = setTimeout(function () {
          purchaseTimer = null;
          if (resetAttempts) purchaseAttempts = 0;
          flushPurchaseOutbox();
        }, delay);
      }
      function purchaseDeliveryFinished() {
        purchaseSending = false;
        if (purchaseOutbox().length) {
          schedulePurchaseFlush(purchaseAttempts < 4 ? 1000 * purchaseAttempts : 60000, purchaseAttempts >= 4);
        }
      }
      function flushPurchaseOutbox() {
        var pending = pendingPurchases;
        pendingPurchases = [];
        pending.forEach(function (item) {
          try {
            if (STORAGE_BLOCKED) record(item.event);
            else localStorage.setItem(PURCHASE_PREFIX + item.event.event_id, JSON.stringify(item.event));
          } catch (error) { record(item.event); }
          try { if (item.afterQueued) item.afterQueued(); } catch (error) {}
        });
        if (purchaseSending || purchaseAttempts >= 4) return;
        var entries = purchaseOutbox().slice(0, 5);
        var c = cfg();
        if (!entries.length || !c.endpoint || c.endpoint.indexOf("REPLACE") !== -1) return;
        purchaseSending = true;
        purchaseAttempts++;
        var envelope = { v: SCHEMA_VERSION, brand: c.brand, token: c.token,
          acknowledge_purchase: true, events: entries.map(function (entry) { return entry.event; }) };
        var abort = new AbortController();
        var deadlineTimer;
        var deadline = new Promise(function (_resolve, reject) {
          deadlineTimer = setTimeout(function () { abort.abort(); reject(new Error("diagnostic_timeout")); }, 10000);
        });
        try {
          // Only diagnostics time out. Stable event IDs make lost-ack retries
          // safe; this abort never reaches a purchase request.
          var delivery = Promise.resolve(fetch(c.endpoint, { method: "POST", mode: "cors", credentials: "omit",
            body: new Blob([JSON.stringify(envelope)], { type: "text/plain" }), keepalive: true, signal: abort.signal }))
            .then(function (response) { return response.status === 200 ? response.json() : null; });
          Promise.race([delivery, deadline]).then(function (receipt) {
            var ids = receipt && receipt.persisted_event_ids;
            if (!(ids instanceof Array)) return;
            entries.forEach(function (entry) {
              if (ids.indexOf(entry.event.event_id) !== -1) localStorage.removeItem(entry.key);
            });
            if (entries.every(function (entry) { return ids.indexOf(entry.event.event_id) !== -1; })) {
              purchaseAttempts = 0;
            }
          }).catch(function () {}).finally(function () {
            clearTimeout(deadlineTimer); purchaseDeliveryFinished();
          });
        } catch (err) { clearTimeout(deadlineTimer); purchaseDeliveryFinished(); }
      }
      window.__cwObsvPurchaseFlow = function (detail, afterQueued) {
        try {
          if (purchaseCount >= PURCHASE_CAP || !detail) return;
          if (!/^[0-9a-f-]{36}$/.test(detail.intentId || "") ||
              !/^bb-v[0-9]{1,3}-c[0-9]{1,4}$/.test(detail.routeMarker || "")) return;
          if (["attempt", "unknown", "recovered", "confirmed", "receipt_missing", "pre_add_failure", "buybox_fallback"].indexOf(detail.phase) === -1) return;
          purchaseCount++;
          var event = baseEvent("buy_box_purchase");
          event.purchase_intent_id = detail.intentId;
          event.purchase_phase = detail.phase;
          event.purchase_reason = /^[a-z_]{0,64}$/.test(detail.reason || "") ? (detail.reason || "") : "unknown";
          event.route_marker = detail.routeMarker;
          pendingPurchases.push({event: event, afterQueued: typeof afterQueued === "function" ? afterQueued : null});
          schedulePurchaseFlush(0);
          return true;
        } catch (err) {}
      };
      window.addEventListener("online", function () {
        purchaseAttempts = 0;
        clearTimeout(purchaseTimer); purchaseTimer = null;
        flushPurchaseOutbox();
      });
      window.addEventListener("pagehide", flushPurchaseOutbox);
      schedulePurchaseFlush(0);

      // ---- cart_add_error bridge (PR-B / B3) — ATC failure telemetry + offline outbox ---------
      // A PUBLIC bridge the theme's product-form catch / non-ok branches call, either as a
      // document event (document.dispatchEvent(new CustomEvent('cw:cart_add_error',
      // {detail:{status,phase,retried}}))) OR the global window.__cwObsvCartError(detail).
      // It closes the fetch-wrapper's 4xx blind spot (that wrapper only emits network_failure on
      // status >= 500 or a throw) at the ONE call site that matters — add-to-cart. Because an
      // OFFLINE add-to-cart also can't beacon, every event is persisted to a small localStorage
      // OUTBOX that DRAINS on the next pageload, so offline failures surface after the fact.
      // cart_add_error is a NEW ESSENTIAL event_type (never analytics-gated; not in
      // ANALYTICS_EVENT_TYPES) and reuses the same MAX_PER_SESSION-style flood guard + transport.
      // Downstream must recognize event_type 'cart_add_error' (edge scrub allowlist + dbt); a
      // buffered event re-recorded next pageload keeps its event_id, so a double-send is
      // downstream-idempotent (dedupe by event_id).
      var MAX_CART_ADD_ERR_PER_SESSION = 20;   // own budget — never gated on ERROR_COUNT
      var CART_ADD_ERR_COUNT = 0;
      var CART_ADD_ERR_LS_KEY = "cw_obsv_cart_add_err_outbox";
      var CART_ADD_ERR_LS_CAP = 20;            // ring the outbox so a flaky session can't grow it unbounded
      // bb_failed / bb_retry_recovered: the buy box's own two phases
      // (assets/buybox.js, R1a in docs/research/boops-bb01-r-marker-4794-2026-09-29.md
      // §7.1), so its write failures stay apart from the product form's rows.
      function cartAddErrPhase(phase) {
        return (phase === "thrown" || phase === "http_error" || phase === "retry_recovered" ||
                phase === "probe_recovered" || phase === "failed" ||
                phase === "bb_failed" || phase === "bb_retry_recovered") ? phase : "failed";
      }
      function buildCartAddError(detail) {
        var ev = baseEvent("cart_add_error");
        ev.error_name = "";
        ev.cart_add_status = (detail && typeof detail.status === "number") ? (detail.status | 0) : 0;
        ev.cart_add_phase = cartAddErrPhase(detail && detail.phase);
        ev.cart_add_retried = !!(detail && detail.retried);
        if (ev.cart_add_phase === "bb_failed" || ev.cart_add_phase === "bb_retry_recovered") {
          addBuyboxWriteRecord(ev, detail);
        }
        ev.fingerprint = computeFingerprint("cart_add_error", ev.cart_add_phase + " " + ev.cart_add_status, "");
        ev.fingerprint_version = FINGERPRINT_VERSION;
        return ev;
      }
      // R1d (BB-01 plan rev 3, ruling R1-5): the buy box's R1a write record
      // (assets/buybox.js `writeRecordDetail`). ONLY on the two bb_* phases, so a
      // product-form row is exactly what it was. A fixed set of scalar fields,
      // each checked alone with the edge's own rules
      // (webhook-ingress/clientlog_scrub.py `_scrub_cart_add_write_record`): closed
      // enums (an unknown reason becomes `other`), attempts 1..3, whole-number
      // durations >= 0 clamped to 120000 ms. A bad value is left out, never sent.
      var BB_WRITES = { clear: 1, write1: 1, write2: 1, write_all: 1 };
      var BB_REASONS = { cart_clear_failed: 1, cart_clear_incomplete: 1, cart_add_failed: 1, other: 1 };
      var BB_MS_CAP = 120000;
      var BB_MS_FIELDS = [
        ["failedWriteMs", "cart_add_failed_write_ms"], ["clearMs", "cart_add_clear_ms"],
        ["write1Ms", "cart_add_write1_ms"], ["write2Ms", "cart_add_write2_ms"],
        ["writeAllMs", "cart_add_write_all_ms"]
      ];
      function bbOwn(map, key) {
        return typeof key === "string" && Object.prototype.hasOwnProperty.call(map, key);
      }
      function addBuyboxWriteRecord(ev, detail) {
        if (!detail) return;
        if (bbOwn(BB_WRITES, detail.failedWrite)) ev.cart_add_failed_write = detail.failedWrite;
        if (typeof detail.reason === "string" && detail.reason) {
          ev.cart_add_reason = bbOwn(BB_REASONS, detail.reason) ? detail.reason : "other";
        }
        var attempts = detail.attempts;
        if (typeof attempts === "number" && attempts % 1 === 0 && attempts >= 1 && attempts <= 3) {
          ev.cart_add_attempts = attempts;
        }
        for (var i = 0; i < BB_MS_FIELDS.length; i++) {
          var ms = detail[BB_MS_FIELDS[i][0]];
          if (typeof ms === "number" && isFinite(ms) && ms >= 0) {
            ev[BB_MS_FIELDS[i][1]] = Math.min(Math.round(ms), BB_MS_CAP);
          }
        }
      }
      function bufferCartAddError(ev) {
        if (STORAGE_BLOCKED) return;
        try {
          var raw = window.localStorage.getItem(CART_ADD_ERR_LS_KEY);
          var arr = raw ? JSON.parse(raw) : [];
          if (!(arr instanceof Array)) arr = [];
          arr.push(ev);
          if (arr.length > CART_ADD_ERR_LS_CAP) arr = arr.slice(arr.length - CART_ADD_ERR_LS_CAP);
          window.localStorage.setItem(CART_ADD_ERR_LS_KEY, JSON.stringify(arr));
        } catch (e) {}
      }
      function onCartAddError(detail) {
        try {
          if (CART_ADD_ERR_COUNT >= MAX_CART_ADD_ERR_PER_SESSION) return;
          CART_ADD_ERR_COUNT++;
          var ev = buildCartAddError(detail);
          record(ev);              // this-page beacon attempt (flushes on visibilitychange/pagehide)
          bufferCartAddError(ev);  // durable outbox -> drains next pageload (offline-safe)
        } catch (e) {}
      }
      try {
        document.addEventListener("cw:cart_add_error", function (e) {
          try { onCartAddError(e && e.detail); } catch (err) {}
        });
        window.__cwObsvCartError = function (detail) { try { onCartAddError(detail); } catch (e) {} };
      } catch (e) {}
      // Drain the offline outbox on THIS pageload (the "flush on next pageload" half): re-record
      // buffered events (same event_id -> downstream-idempotent) then clear the key so they get
      // one fresh delivery attempt this load.
      (function drainCartAddErrorOutbox() {
        if (STORAGE_BLOCKED) return;
        try {
          var raw = window.localStorage.getItem(CART_ADD_ERR_LS_KEY);
          if (!raw) return;
          window.localStorage.removeItem(CART_ADD_ERR_LS_KEY);
          var arr = JSON.parse(raw);
          if (!(arr instanceof Array)) return;
          for (var i = 0; i < arr.length && i < CART_ADD_ERR_LS_CAP; i++) {
            if (arr[i] && arr[i].event_type === "cart_add_error") record(arr[i]);
          }
        } catch (e) {}
      })();

      // ---- cart_change_error bridge (cart-drawer build standards R5.2) -------------------------
      // The cart_add_error twin for in-cart MUTATIONS (qty change / plan switch via
      // /cart/change.js): the theme's hardened mutation error paths dispatch
      // document.dispatchEvent(new CustomEvent('cw:cart_change_error',
      // {detail:{status,phase,op}})) — this closes the same fetch-wrapper 4xx blind spot at the
      // cart-editing call sites (stale line index, sold-out bump, 429 bot-protection). Same
      // ESSENTIAL posture, own flood budget, same offline outbox pattern (a change that failed
      // because the shopper went offline can't beacon live either). Downstream must recognize
      // event_type 'cart_change_error' (edge scrub allowlist + dbt) — until that follow-up lands
      // the edge may drop these; the theme side is forward-compatible and idempotent.
      var MAX_CART_CHANGE_ERR_PER_SESSION = 20;   // own budget — never gated on ERROR_COUNT
      var CART_CHANGE_ERR_COUNT = 0;
      var CART_CHANGE_ERR_LS_KEY = "cw_obsv_cart_change_err_outbox";
      var CART_CHANGE_ERR_LS_CAP = 20;
      function cartChangeErrPhase(phase) {
        return (phase === "thrown" || phase === "http_error") ? phase : "http_error";
      }
      function cartChangeErrOp(op) {
        return (op === "qty" || op === "plan" || op === "remove" || op === "discount" || op === "note") ? op : "qty";
      }
      function buildCartChangeError(detail) {
        var ev = baseEvent("cart_change_error");
        ev.error_name = "";
        ev.cart_change_status = (detail && typeof detail.status === "number") ? (detail.status | 0) : 0;
        ev.cart_change_phase = cartChangeErrPhase(detail && detail.phase);
        ev.cart_change_op = cartChangeErrOp(detail && detail.op);
        ev.fingerprint = computeFingerprint("cart_change_error", ev.cart_change_op + " " + ev.cart_change_phase + " " + ev.cart_change_status, "");
        ev.fingerprint_version = FINGERPRINT_VERSION;
        return ev;
      }
      function bufferCartChangeError(ev) {
        if (STORAGE_BLOCKED) return;
        try {
          var raw = window.localStorage.getItem(CART_CHANGE_ERR_LS_KEY);
          var arr = raw ? JSON.parse(raw) : [];
          if (!(arr instanceof Array)) arr = [];
          arr.push(ev);
          if (arr.length > CART_CHANGE_ERR_LS_CAP) arr = arr.slice(arr.length - CART_CHANGE_ERR_LS_CAP);
          window.localStorage.setItem(CART_CHANGE_ERR_LS_KEY, JSON.stringify(arr));
        } catch (e) {}
      }
      try {
        document.addEventListener("cw:cart_change_error", function (e) {
          try {
            if (CART_CHANGE_ERR_COUNT >= MAX_CART_CHANGE_ERR_PER_SESSION) return;
            CART_CHANGE_ERR_COUNT++;
            var ev = buildCartChangeError(e && e.detail);
            record(ev);                 // this-page beacon attempt
            bufferCartChangeError(ev);  // durable outbox -> drains next pageload
          } catch (err) {}
        });
      } catch (e) {}
      (function drainCartChangeErrorOutbox() {
        if (STORAGE_BLOCKED) return;
        try {
          var raw = window.localStorage.getItem(CART_CHANGE_ERR_LS_KEY);
          if (!raw) return;
          window.localStorage.removeItem(CART_CHANGE_ERR_LS_KEY);
          var arr = JSON.parse(raw);
          if (!(arr instanceof Array)) return;
          for (var i = 0; i < arr.length && i < CART_CHANGE_ERR_LS_CAP; i++) {
            if (arr[i] && arr[i].event_type === "cart_change_error") record(arr[i]);
          }
        } catch (e) {}
      })();

      // ---- free-shipping telemetry bridge (consolidation plan §8 W-ALARM, ruling R11) ----------
      // snippets/free-shipping-line-config.liquid SHOUTS but never leaves the browser: its
      // alertOnce() and recordRelease() end in a window array, a document CustomEvent and a
      // console.warn. Nothing listened until this block. It carries TWO ESSENTIAL events over the
      // SAME record() + outbox + beacon path as cart_add_error (§3.7):
      //   free_shipping_alert   — every cw:free_shipping_alert (kind, benefitKey, bounded detail);
      //   free_shipping_release — EVERY intercepted checkout release (I3, operator 2026-10-01: the
      //                           benchmark needs the denominator and the normal latency
      //                           distribution, not only the abnormal tail). Volume: one per
      //                           intercepted checkout click, about 26/day (547 drawer + cart
      //                           checkout entries in 21 days, GA4 2026-09-11..10-01), so no
      //                           sampling (sample_rate stays 1.0). Until 2026-10-01 only abnormal
      //                           releases were carried; rows from the new asset carry
      //                           fs_release_add_state, which is how a read tells the two apart.
      // CRITICAL PATH: the FS script dispatches the release just BEFORE requestSubmit() /
      // navigation, so the listener only queues the payload. The record + outbox write run on the
      // next task, or at page-hide if that comes first (#10952 round 3 P1-3: no synchronous storage
      // in the release callback). No network request is added: the release rides the page-hide
      // beacon like every other essential event.
      // LOAD ORDER: theme.liquid renders free-shipping-line-config at :33 and this snippet at :58,
      // so a listener registered here MISSES anything dispatched earlier — including
      // invalid_free_shipping_configuration, which fires at arm time. The snippet also appends
      // every payload to window.__cwFreeShippingAlerts / window.__cwFreeShippingReleases, so on
      // load this block DRAINS both arrays once, then listens. Pre-load payloads come from the
      // arrays, post-load payloads from the events; nothing is counted twice.
      // No PII by construction: kind/result/path are client enums, detail is a one-level walk with
      // URL-ish strings reduced to their path, every scalar capped, and trailing keys DROPPED (never
      // sliced mid-string) until the JSON fits, so the field always parses. The edge
      // (clientlog_scrub.py §3.9/§3.10) is the canonical scrub boundary and re-strips it.
      // Budgets are PER PAGE LOAD (page-local vars, like MAX_PER_SESSION above): alertOnce dedupes
      // per kind per page, and the arm-time invalid_free_shipping_configuration alert therefore
      // repeats once per page view for as long as the store is misconfigured — by design, it is
      // the alarm.
      var MAX_FS_ALERT_PER_SESSION = 20;     // own budget — never gated on ERROR_COUNT
      var MAX_FS_RELEASE_PER_SESSION = 8;    // one per intercepted checkout attempt
      var FS_ALERT_COUNT = 0;
      var FS_RELEASE_COUNT = 0;
      var FS_LS_KEY = "cw_obsv_free_shipping_outbox";
      var FS_RELEASE_PENDING = [];
      function drainFreeShippingReleases() {
        try {
          var pending = FS_RELEASE_PENDING;
          FS_RELEASE_PENDING = [];
          for (var i = 0; i < pending.length; i++) onFreeShippingRelease(pending[i]);
        } catch (e) {}
      }
      var FS_LS_CAP = 20;
      var FS_DETAIL_CAP = 512;
      var FS_ENUM_CAP = 64;
      function fsEnum(value, fallback) {
        try {
          if (typeof value !== "string" || !value) return fallback;
          return value.slice(0, FS_ENUM_CAP);
        } catch (e) { return fallback; }
      }
      function fsInt(value) {
        try {
          var n = Number(value);
          if (typeof n !== "number" || n !== n || n < 0) return 0;
          return Math.round(n);
        } catch (e) { return 0; }
      }
      function fsDetailScalar(value) {
        if (value === null || value === undefined) return null;
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return (value === value) ? value : null;
        if (typeof value === "string") {
          if (/^(https?:)?\/\//i.test(value) || value.charAt(0) === "/") return pathOnly(value).slice(0, 80);
          return value.slice(0, 80);
        }
        return String(value).slice(0, 80);
      }
      function boundedFsDetail(detail) {
        try {
          if (!detail || typeof detail !== "object") return "";
          var out = {};
          var kept = [];
          var keys = [];
          for (var k in detail) if (Object.prototype.hasOwnProperty.call(detail, k)) keys.push(k);
          for (var i = 0; i < keys.length && i < 12; i++) {
            var v = detail[keys[i]];
            if (v instanceof Array) {
              var items = [];
              for (var j = 0; j < v.length && j < 10; j++) items.push(fsDetailScalar(v[j]));
              out[keys[i]] = items;
            } else if (v && typeof v === "object") {
              out[keys[i]] = "<object>";
            } else {
              out[keys[i]] = fsDetailScalar(v);
            }
            kept.push(keys[i]);
          }
          var s = JSON.stringify(out) || "";
          // Never slice the JSON: drop trailing keys until it fits, and say so.
          while (s.length > FS_DETAIL_CAP && kept.length) {
            delete out[kept.pop()];
            out._truncated = true;
            s = JSON.stringify(out) || "";
          }
          return s.length > FS_DETAIL_CAP ? "" : s;
        } catch (e) { return ""; }
      }
      function buildFreeShippingAlert(payload) {
        var ev = baseEvent("free_shipping_alert");
        ev.error_name = "";
        ev.fs_alert_kind = fsEnum(payload && payload.kind, "unknown");
        ev.fs_benefit_key = fsEnum(payload && payload.benefitKey, "");
        ev.fs_alert_detail = boundedFsDetail(payload && payload.detail);
        return ev;
      }
      function buildFreeShippingRelease(payload) {
        var ev = baseEvent("free_shipping_release");
        ev.error_name = "";
        ev.fs_benefit_key = fsEnum(payload.benefitKey, "");
        ev.fs_release_ms = fsInt(payload.ms);
        ev.fs_release_active_work_ms = fsInt(payload.activeWorkMs);
        ev.fs_release_main_thread_suspension_ms = fsInt(payload.mainThreadSuspensionMs);
        ev.fs_release_path = fsEnum(payload.path, "unknown");
        ev.fs_release_result = fsEnum(payload.result, "unknown");
        ev.fs_release_network_class = fsEnum(payload.networkClass, "unknown");
        ev.fs_release_browser_class = fsEnum(payload.browserClass, "unknown");
        ev.fs_release_known_unsafe_line_count = fsInt(payload.knownUnsafeLineCount);
        ev.fs_release_budget_ms = fsInt(payload.budgetMs);
        ev.fs_release_over_budget = payload.overBudget === true;
        // I3 benchmark fields. Absent on a payload from the previous FS asset.
        if (typeof payload.addState === "string" && payload.addState) {
          ev.fs_release_add_state = fsEnum(payload.addState, "unknown");
          ev.fs_release_line_in_time = payload.lineInTime === true;
        }
        return ev;
      }
      function bufferFreeShipping(ev) {
        if (STORAGE_BLOCKED) return;
        try {
          var raw = window.localStorage.getItem(FS_LS_KEY);
          var arr = raw ? JSON.parse(raw) : [];
          if (!(arr instanceof Array)) arr = [];
          arr.push(ev);
          if (arr.length > FS_LS_CAP) arr = arr.slice(arr.length - FS_LS_CAP);
          window.localStorage.setItem(FS_LS_KEY, JSON.stringify(arr));
        } catch (e) {}
      }
      function onFreeShippingAlert(payload) {
        try {
          if (FS_ALERT_COUNT >= MAX_FS_ALERT_PER_SESSION) return;
          FS_ALERT_COUNT++;
          var ev = buildFreeShippingAlert(payload);
          record(ev);               // this-page beacon attempt (flushes on visibilitychange/pagehide)
          bufferFreeShipping(ev);   // durable outbox -> drains next pageload (offline-safe)
        } catch (e) {}
      }
      function onFreeShippingRelease(payload) {
        try {
          if (!payload || typeof payload !== "object") return;
          if (FS_RELEASE_COUNT >= MAX_FS_RELEASE_PER_SESSION) return;
          FS_RELEASE_COUNT++;
          var ev = buildFreeShippingRelease(payload);
          record(ev);
          bufferFreeShipping(ev);
        } catch (e) {}
      }
      try {
        document.addEventListener("cw:free_shipping_alert", function (e) {
          try { onFreeShippingAlert(e && e.detail); } catch (err) {}
        });
        document.addEventListener("cw:free_shipping_release", function (e) {
          // Queue only: this runs inside the checkout click, before the navigation.
          try {
            FS_RELEASE_PENDING.push(e && e.detail);
            if (FS_RELEASE_PENDING.length === 1) setTimeout(drainFreeShippingReleases, 0);
          } catch (err) {}
        });
      } catch (e) {}
      // Drain the offline outbox on THIS pageload (same event_id -> downstream-idempotent).
      (function drainFreeShippingOutbox() {
        if (STORAGE_BLOCKED) return;
        try {
          var raw = window.localStorage.getItem(FS_LS_KEY);
          if (!raw) return;
          window.localStorage.removeItem(FS_LS_KEY);
          var arr = JSON.parse(raw);
          if (!(arr instanceof Array)) return;
          for (var i = 0; i < arr.length && i < FS_LS_CAP; i++) {
            var t = arr[i] && arr[i].event_type;
            if (t === "free_shipping_alert" || t === "free_shipping_release") record(arr[i]);
          }
        } catch (e) {}
      })();
      // Drain what the snippet dispatched BEFORE this listener existed (load-order trap above).
      (function drainFreeShippingPreload() {
        try {
          var alerts = window.__cwFreeShippingAlerts;
          if (alerts instanceof Array) {
            for (var i = 0; i < alerts.length; i++) onFreeShippingAlert(alerts[i]);
          }
        } catch (e) {}
        try {
          var releases = window.__cwFreeShippingReleases;
          if (releases instanceof Array) {
            for (var j = 0; j < releases.length; j++) onFreeShippingRelease(releases[j]);
          }
        } catch (e) {}
      })();

      // STAGE 2 — ANALYTICS RUM (deferred after LCP, sampled, consent-gated)
      function vitalRating(name, value) {
        // thresholds per contract §3.4
        if (name === "LCP")  return value <= 2500 ? "good" : (value > 4000 ? "poor" : "needs-improvement");
        if (name === "INP")  return value <= 200  ? "good" : (value > 500  ? "poor" : "needs-improvement");
        if (name === "CLS")  return value <= 0.1  ? "good" : (value > 0.25 ? "poor" : "needs-improvement");
        if (name === "FCP")  return value <= 1800 ? "good" : (value > 3000 ? "poor" : "needs-improvement");
        if (name === "TTFB") return value <= 800  ? "good" : (value > 1800 ? "poor" : "needs-improvement");
        return "needs-improvement";
      }

      function emitVital(name, value, attr) {
        try {
          var ev = baseEvent("web_vitals");
          var c = cfg();
          ev.sample_rate = (typeof c.sampleRumPct === "number" && c.sampleRumPct > 0) ? c.sampleRumPct : 1.0;
          ev.vital_name = name;
          ev.vital_value = value;
          ev.vital_rating = vitalRating(name, value);
          attr = attr || {};
          ev.vital_attr_element = attr.element || "";
          ev.vital_attr_url = attr.url ? pathOnly(attr.url) : "";
          ev.vital_attr_ttfb = (typeof attr.ttfb === "number") ? attr.ttfb : null;
          ev.vital_attr_render_delay = (typeof attr.renderDelay === "number") ? attr.renderDelay : null;
          ev.vital_attr_interaction_target = attr.interactionTarget || "";
          ev.vital_attr_longest_script_ms = (typeof attr.longestScriptMs === "number") ? attr.longestScriptMs : null;
          record(ev);
        } catch (e) {}
      }

      function emitTiming(timingName, ms) {
        try {
          if (!RUM_SAMPLED_IN) return;
          var ev = baseEvent("custom_timing");
          var c = cfg();
          ev.sample_rate = (typeof c.sampleRumPct === "number" && c.sampleRumPct > 0) ? c.sampleRumPct : 1.0;
          if (timingName.indexOf("buybox_") === 0 && typeof window.__cwBuyboxAssetVersion === "string") {
            ev.theme_asset_version = window.__cwBuyboxAssetVersion;
          }
          ev.timing_name = timingName;
          ev.timing_ms = ms;
          record(ev);
        } catch (e) {}
      }

      // P1C (docs/plans/boops-buybox-page-load-audit-and-plan-2026-10-08.md §6 item
      // 10). "When did deferred scripts get to run": the navigation entry's
      // HTML-arrival, DOM-interactive, DOMContentLoaded and load marks, all ms
      // since navigation start. Read on the page-hide finalizer because the
      // marks are 0 until the browser reaches them; a hide before DOMContentLoaded
      // omits them and the next hide retries. Sampled custom_timing rows (no new
      // event type, no edge or schema change); a mark of 0 or less is never sent.
      var NAV_TIMING_MARKS = {
        nav_response_end_ms: "responseEnd",
        nav_dom_interactive_ms: "domInteractive",
        nav_dcl_start_ms: "domContentLoadedEventStart",
        nav_dcl_end_ms: "domContentLoadedEventEnd",
        nav_load_start_ms: "loadEventStart"
      };
      var navTimingsSent = false;
      function emitNavTimings() {
        try {
          if (navTimingsSent) return;
          var nav = performance.getEntriesByType("navigation")[0];
          if (!nav || !(nav.domContentLoadedEventStart > 0)) return;
          navTimingsSent = true;
          for (var name in NAV_TIMING_MARKS) {
            var v = nav[NAV_TIMING_MARKS[name]];
            if (typeof v === "number" && isFinite(v) && v > 0) emitTiming(name, Math.round(v));
          }
        } catch (e) {}
      }

      var pendingBuyboxTimings = {};
      window.__cwObsvTiming = function (name, value) {
        if (/^buybox_(initial_cards_ms|first_card_ms|tap_to_add_ms|add_rtt_ms|arrival_cart_lines|ready_ms)$/.test(name)
            && typeof value === "number" && isFinite(value) && value >= 0) {
          if (SENT_RUM) emitTiming(name, value);
          else pendingBuyboxTimings[name] = value;
        }
      };

      function safePO(type, opts, cb) {
        try {
          if (typeof PerformanceObserver !== "function") return null;
          var po = new PerformanceObserver(cb);
          var o = { type: type, buffered: true };
          if (opts) { for (var k in opts) if (opts.hasOwnProperty(k)) o[k] = opts[k]; }
          po.observe(o);
          return po;
        } catch (e) { return null; }
      }

      // Compact inline RUM. The official google/web-vitals attribution build can be
      // vendored later for richer LoAF attribution; this stays within the byte budget.
      function selectRumSample() {
        var c = cfg();
        if (!c.enableRum || SENT_RUM) return;
        SENT_RUM = true;
        RUM_SAMPLED_IN = rand() < (c.sampleRumPct || 0);
        for (var timingName in pendingBuyboxTimings) emitTiming(timingName, pendingBuyboxTimings[timingName]);
        pendingBuyboxTimings = {};
      }

      function startRum() {
        try {
          selectRumSample();
          if (!RUM_SAMPLED_IN) return;

          // ---- TTFB (navigation) ----
          try {
            var nav = performance.getEntriesByType("navigation")[0];
            if (nav && typeof nav.responseStart === "number") {
              emitVital("TTFB", Math.max(0, nav.responseStart), { ttfb: nav.responseStart });
            }
          } catch (e) {}

          // ---- FCP (paint) ----
          safePO("paint", null, function (list) {
            try {
              var entries = list.getEntries();
              for (var i = 0; i < entries.length; i++) {
                if (entries[i].name === "first-contentful-paint") {
                  emitVital("FCP", entries[i].startTime, {});
                }
              }
            } catch (e) {}
          });

          // ---- LCP (largest-contentful-paint) — last candidate wins, flushed at hidden ----
          var lcpPO = safePO("largest-contentful-paint", null, function (list) {
            try {
              var entries = list.getEntries();
              var last = entries[entries.length - 1];
              if (!last) return;
              var renderDelay = null;
              try {
                var navEntry = performance.getEntriesByType("navigation")[0];
                var ttfbVal = navEntry ? navEntry.responseStart : 0;
                renderDelay = Math.max(0, last.renderTime ? (last.renderTime - ttfbVal) : 0);
              } catch (re) {}
              rumLcpValue = {
                value: last.renderTime || last.loadTime || last.startTime,
                element: last.element ? cssSelector(last.element) : "",
                url: last.url || "",
                renderDelay: renderDelay
              };
            } catch (e) {}
          });

          // ---- CLS (layout-shift) — cumulative, largest-shift target ----
          var clsValue = 0, clsTarget = "";
          safePO("layout-shift", null, function (list) {
            try {
              var entries = list.getEntries();
              var maxShift = 0;
              for (var i = 0; i < entries.length; i++) {
                var entry = entries[i];
                if (entry.hadRecentInput) continue;
                clsValue += entry.value;
                if (entry.value > maxShift) {
                  maxShift = entry.value;
                  try {
                    var src = entry.sources && entry.sources[0] && entry.sources[0].node;
                    if (src) clsTarget = cssSelector(src);
                  } catch (te) {}
                }
              }
            } catch (e) {}
          });

          // ---- INP / first-input (event timing) + LoAF longest-script if available ----
          var worstInp = 0, inpTarget = "", longestScriptMs = null;
          safePO("event", { durationThreshold: 40 }, function (list) {
            try {
              var entries = list.getEntries();
              for (var i = 0; i < entries.length; i++) {
                var entry = entries[i];
                var dur = entry.duration || 0;
                if (dur > worstInp) {
                  worstInp = dur;
                  inpTarget = entry.target ? cssSelector(entry.target) : "";
                }
              }
            } catch (e) {}
          });
          safePO("first-input", null, function (list) {
            try {
              var entries = list.getEntries();
              var fi = entries[0];
              if (fi) {
                var dur = (fi.processingStart && fi.startTime) ? (fi.processingStart - fi.startTime) : (fi.duration || 0);
                if (dur > worstInp) {
                  worstInp = dur;
                  inpTarget = fi.target ? cssSelector(fi.target) : "";
                }
              }
            } catch (e) {}
          });
          safePO("long-animation-frame", null, function (list) {
            try {
              var entries = list.getEntries();
              for (var i = 0; i < entries.length; i++) {
                var scripts = entries[i].scripts || [];
                for (var j = 0; j < scripts.length; j++) {
                  var d = scripts[j].duration || 0;
                  if (longestScriptMs === null || d > longestScriptMs) longestScriptMs = d;
                }
              }
            } catch (e) {}
          });

          // Finalize CLS / LCP / INP on hidden (the canonical reporting moment).
          addEventListener("visibilitychange", function () {
            try {
              if (document.visibilityState !== "hidden") return;
              if (lcpPO && lcpPO.takeRecords) { try { lcpPO.takeRecords(); } catch (e) {} }
              if (rumLcpValue) {
                emitVital("LCP", rumLcpValue.value, {
                  element: rumLcpValue.element, url: rumLcpValue.url, renderDelay: rumLcpValue.renderDelay
                });
                rumLcpValue = null;
              }
              emitNavTimings();
              if (clsValue > 0) emitVital("CLS", clsValue, { element: clsTarget });
              if (worstInp > 0) {
                emitVital("INP", worstInp, { interactionTarget: inpTarget, longestScriptMs: longestScriptMs });
                if (longestScriptMs !== null) emitTiming("inp_loaf_tbt_proxy", longestScriptMs);
              }
              // CRITICAL ordering: this finalizer is registered LATE (inside the deferred
              // requestIdleCallback startRum()), so the transport flush() listener — registered
              // EARLY in the IIFE — already ran on this same visibilitychange and drained an
              // empty BUF before these vitals existed. There is no pagehide on a tab-switch/discard,
              // so without a flush here the LCP/CLS/INP we just recorded would never send. Flush
              // now so the vitals emitted above leave on this page-hide.
              flush();
            } catch (e) {}
          });
        } catch (e) {}
      }

      // ---- custom business timings (predictive search, drawer open) ----
      function startCustomTimings() {
        try {
          if (!RUM_SAMPLED_IN) return;

          // predictive_search_latency — observe predictive-search fetch round-trip.
          var lastSearchAt = 0;
          try {
            document.addEventListener("input", function (e) {
              try {
                var t = e.target;
                if (t && t.closest && t.closest("predictive-search, [data-predictive-search]")) {
                  lastSearchAt = nowMs();
                }
              } catch (err) {}
            }, true);
          } catch (e) {}
          // network observer for /search/suggest -> measure latency
          safePO("resource", null, function (list) {
            try {
              var entries = list.getEntries();
              for (var i = 0; i < entries.length; i++) {
                var name = entries[i].name || "";
                if (name.indexOf("/search/suggest") !== -1 && lastSearchAt) {
                  emitTiming("predictive_search_latency", entries[i].responseEnd - entries[i].startTime);
                  lastSearchAt = 0;
                }
              }
            } catch (e) {}
          });

          // drawer_open_success / drawer_open_failure — theme dispatches cart:change
          // (drawer opens) on success and cart:error on a failed add. Mark on submit,
          // measure to the first resolution event.
          var addToCartMarkAt = 0;
          try {
            document.addEventListener("submit", function (e) {
              try {
                var f = e && e.target;
                if (f && f.action && f.action.indexOf("/cart/add") !== -1) addToCartMarkAt = nowMs();
              } catch (err) {}
            }, true);
            document.addEventListener("variant:add", function () {
              try {
                if (addToCartMarkAt) {
                  emitTiming("time_to_add_to_cart_success", nowMs() - addToCartMarkAt);
                  emitTiming("drawer_open_success", nowMs() - addToCartMarkAt);
                  addToCartMarkAt = 0;
                }
              } catch (err) {}
            }, true);
            document.addEventListener("cart:error", function () {
              try {
                if (addToCartMarkAt) {
                  emitTiming("time_to_add_to_cart_failure", nowMs() - addToCartMarkAt);
                  emitTiming("drawer_open_failure", nowMs() - addToCartMarkAt);
                  addToCartMarkAt = 0;
                }
              } catch (err) {}
            }, true);
          } catch (e) {}
        } catch (e) {}
      }

      // Defer Stage 2 until after LCP. requestIdleCallback with a setTimeout(cb, 1)
      // fallback (rIC is absent on older iOS WebViews — the exact in-app cohort).
      //
      // DO NOT read cfg().enableRum here to gate SCHEDULING. deferStage2 runs at
      // PARSE time — theme.liquid renders this snippet at :18, BEFORE js-variables
      // .liquid populates window.themeVariables at :63 (see the module header) — so
      // an eager read returns the enableRum:false default and would permanently
      // kill Stage-2 RUM (the 0-web_vitals-ever defect: every essential event flowed
      // because those paths read cfg() LAZILY at event/flush time; RUM alone gated
      // its scheduling on a parse-time read). Schedule UNCONDITIONALLY and let
      // startRum()/startCustomTimings() read cfg() lazily INSIDE the deferred
      // callback (themeVariables exists by then) and self-gate on enableRum +
      // sampling — mirroring every Stage-1 path.
      (function deferStage2() {
        try {
          var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 1); };
          idle(function () {
            startRum();
            startCustomTimings();
          }, { timeout: 3000 });
        } catch (e) {}
      })();

      // TRANSPORT — one batched beacon per page (§1)
      function send(records) {
        try {
          if (!records || !records.length) return;
          var c = cfg();
          if (!c.endpoint) return;
          // js-variables.liquid ships a 'https://...-REPLACE.run.app/...' placeholder until the
          // operator wires the real webhook-ingress Cloud Run URL. That placeholder is truthy, so
          // without this guard every page-hide would sendBeacon to a dead host. Treat REPLACE as a
          // true inert no-op (NOT the same as an empty endpoint, which an operator might also set).
          if (c.endpoint.indexOf("REPLACE") !== -1) return;
          var envelope = { v: SCHEMA_VERSION, brand: c.brand, token: c.token, events: records };
          var body = JSON.stringify(envelope);
          // text/plain is a CORS-safelisted content-type => no preflight (sendBeacon can't preflight).
          var blob = new Blob([body], { type: "text/plain" });
          var ok = false;
          try { ok = !!(navigator.sendBeacon && navigator.sendBeacon(c.endpoint, blob)); } catch (e) { ok = false; }
          if (!ok) {
            try {
              fetch(c.endpoint, { method: "POST", body: blob, keepalive: true, mode: "no-cors" }).catch(function () {});
            } catch (e) {}
          }
        } catch (e) {}
      }

      function flush() {
        try {
          // A release queued by the checkout click is recorded before the page-hide send.
          try { drainFreeShippingReleases(); } catch (e) {}
          if (!BUF.length) return;
          var essential = [], analytics = [];
          var consent = consentState();
          var canSendAnalytics = consent !== "essential_only";
          for (var i = 0; i < BUF.length; i++) {
            var k = kindOf(BUF[i]);
            if (k === "essential") essential.push(BUF[i]);
            else if (canSendAnalytics) {
              BUF[i].consent_state = consent;
              analytics.push(BUF[i]);
            } // explicit decline discards RUM; essential errors still leave
          }
          // Split each class into ≤MAX_EVENTS_PER_BATCH-sized beacons.
          sendBatched(essential);
          if (canSendAnalytics) sendBatched(analytics);
          BUF = [];
        } catch (e) {}
      }

      function sendBatched(records) {
        try {
          for (var i = 0; i < records.length; i += MAX_EVENTS_PER_BATCH) {
            send(records.slice(i, i + MAX_EVENTS_PER_BATCH));
          }
        } catch (e) {}
      }

      try {
        addEventListener("visibilitychange", function () {
          if (document.visibilityState === "hidden") { selectRumSample(); flush(); }
        });
        addEventListener("pagehide", function () { selectRumSample(); flush(); });
        addEventListener("pageshow", function (e) {
          if (e && e.persisted) { ERROR_COUNT = 0; CART_FT_COUNT = 0; NET_COUNT = 0; } // bfcache re-arm (all budgets)
        });
      } catch (e) {}

      try {
        // --- debug transport: micro-batched, keepalive-only, separate sink ---
        // The 64 KB cap is the documented iOS WKWebView fetch({keepalive:true})
        // per-origin body quota (WebKit bug 168865). We flush well under it (32 KB)
        // so a single flush is never silently dropped by the platform.
        var DEBUG_FLUSH_BYTES = 32 * 1024;   // ~32 KB safe margin under the 64 KB keepalive quota
        var DEBUG_FLUSH_DEBOUNCE_MS = 100;   // coalesce a burst of console calls into one request
        var DEBUG_MAX_PER_SESSION = 2000;    // hard cap so a runaway console loop can't flood the sink
        var DEBUG_MAX_BUF = 512;             // memory ceiling on pending debug events
        var DEBUG_MSG_CAP = 4000;            // per-arg stringified length cap

        var DEBUG_BUF = [];                  // pending debug events (NOT the production BUF)
        var DEBUG_BUF_BYTES = 0;             // running byte estimate of DEBUG_BUF payloads
        var DEBUG_COUNT = 0;                 // counts toward DEBUG_MAX_PER_SESSION
        var debugFlushTimer = null;

        // Cheap UTF-8-ish byte estimate (no TextEncoder dependency on old WebViews).
        function debugByteLen(s) {
          try {
            var n = 0, c;
            for (var i = 0; i < s.length; i++) {
              c = s.charCodeAt(i);
              n += c < 0x80 ? 1 : (c < 0x800 ? 2 : 3);
            }
            return n;
          } catch (e) { return s ? s.length : 0; }
        }

        // Stringify one console arg defensively (never throw, scrub like Stage-1, cap length).
        function debugStringifyArg(a) {
          try {
            if (a === null) return "null";
            if (a === undefined) return "undefined";
            var t = typeof a;
            if (t === "string") return scrubText(a).slice(0, DEBUG_MSG_CAP);
            if (t === "number" || t === "boolean") return String(a);
            if (a instanceof Error) {
              return scrubText((a.name || "Error") + ": " + (a.message || "")).slice(0, DEBUG_MSG_CAP);
            }
            var seen = [];
            var json = JSON.stringify(a, function (k, v) {
              if (typeof v === "object" && v !== null) {
                if (seen.indexOf(v) !== -1) return "[Circular]";
                seen.push(v);
              }
              return v;
            });
            return scrubText(json || String(a)).slice(0, DEBUG_MSG_CAP);
          } catch (e) {
            try { return scrubText(String(a)).slice(0, DEBUG_MSG_CAP); } catch (e2) { return "<unserializable>"; }
          }
        }

        // Best-effort caller source (top frame) for source_class classification.
        function debugCallerSource() {
          try {
            var stack = (new Error()).stack || "";
            var lines = String(stack).split("\n");
            // skip frames inside this wrapper; find the first non-observability frame
            for (var i = 0; i < lines.length; i++) {
              var m = lines[i].match(/(https?:\/\/[^\s):]+|\/[^\s):]+\.js)/);
              if (m && m[1].indexOf("observability") === -1) return m[1];
            }
            return "";
          } catch (e) { return ""; }
        }

        function debugFlush() {
          try {
            if (debugFlushTimer) { try { clearTimeout(debugFlushTimer); } catch (e) {} debugFlushTimer = null; }
            if (!DEBUG_BUF.length) return;
            var c = cfg();
            var records = DEBUG_BUF;
            DEBUG_BUF = [];
            DEBUG_BUF_BYTES = 0;
            if (!c.endpoint || c.endpoint.indexOf("REPLACE") !== -1) return; // inert if unwired (same rule as send())
            // is_debug_stream:true so the edge routes this to a throwaway sink, never the
            // canonical clientlog table. keepalive-only — do NOT use sendBeacon (it shares
            // the page-hide beacon's quota; a console flood must never evict a Stage-1 event).
            var envelope = { v: SCHEMA_VERSION, brand: c.brand, token: c.token, is_debug_stream: true, events: records };
            var blob = new Blob([JSON.stringify(envelope)], { type: "text/plain" });
            try {
              fetch(c.endpoint, { method: "POST", body: blob, keepalive: true, mode: "no-cors" }).catch(function () {});
            } catch (e) {}
          } catch (e) {}
        }

        function debugScheduleFlush() {
          try {
            if (DEBUG_BUF_BYTES >= DEBUG_FLUSH_BYTES) { debugFlush(); return; } // size-triggered: flush now
            if (debugFlushTimer) return;                                       // debounce already armed
            debugFlushTimer = setTimeout(debugFlush, DEBUG_FLUSH_DEBOUNCE_MS);
          } catch (e) {}
        }

        // Record one buffered console event. This is the ONLY place that appends —
        // it never sends per-event; the size/debounce policy in debugScheduleFlush owns transport.
        function debugRecord(level, args) {
          try {
            if (DEBUG_COUNT >= DEBUG_MAX_PER_SESSION) return;
            if (DEBUG_BUF.length >= DEBUG_MAX_BUF) { debugFlush(); if (DEBUG_BUF.length >= DEBUG_MAX_BUF) return; }
            DEBUG_COUNT++;
            var parts = [];
            var src = "";
            try {
              for (var i = 0; i < args.length && i < 12; i++) {
                parts.push(debugStringifyArg(args[i]));
                if (!src) { var s = debugStringifyArg(args[i]); if (/https?:\/\/|\.js/.test(s)) src = s; }
              }
            } catch (e) {}
            var caller = debugCallerSource();
            var ev = baseEvent("console_debug");
            ev.debug_level = level;            // "log" | "warn" | "error"
            ev.message = parts.join(" ").slice(0, DEBUG_MSG_CAP);
            ev.source_class = classify(caller || "");
            ev.caller_source = pathOnly(caller || "");
            var bytes = debugByteLen(ev.message) + 256; // payload + envelope overhead estimate
            DEBUG_BUF.push(ev);
            DEBUG_BUF_BYTES += bytes;
            debugScheduleFlush();
          } catch (e) {}
        }

        // The compile-time-gated console wrapper in
        // snippets/observability.liquid forwards here through __cwObsvQ.
        // Nothing in this asset ever reassigns console.
        debugConsoleRecord = debugRecord;

        // Flush the debug buffer on the same page-lifecycle moments as the production beacon.
        try {
          addEventListener("visibilitychange", function () {
            if (document.visibilityState === "hidden") debugFlush();
          });
          addEventListener("pagehide", debugFlush);
        } catch (e) {}
      } catch (e) {
        // debug stream must never break the page either
      }

      // ---- deferred-asset handoff ------------------------------------------
      // snippets/observability.liquid armed a tiny inline stub at head-parse
      // time so nothing is lost while this asset loads. Replay what the stub
      // captured, then make every later stub call dispatch straight through.
      // The real __cwObsvPurchaseFlow / __cwObsvTiming / __cwObsvCartError have
      // already replaced the stub pushers above, by plain assignment.
      // Returns true when the entry was handled, so the stub can relay the
      // runtime's own acceptance contract to its caller.
      function dispatchObsvQueueEntry(entry) {
        try {
          // Array.isArray, not `instanceof Array`: the stub's entries and this
          // asset need not share an Array intrinsic (they do in a browser, but
          // an offline harness realm is a real and useful execution context).
          if (!Array.isArray(entry) || !entry.length) return false;
          switch (entry[0]) {
            case "error": onError(entry[1]); return true;
            case "rejection": onRejection(entry[1]); return true;
            case "resource": onResourceError(entry[1]); return true;
            case "purchase": return window.__cwObsvPurchaseFlow(entry[1], entry[2]) === true;
            case "timing": window.__cwObsvTiming(entry[1], entry[2]); return true;
            case "cartError": window.__cwObsvCartError(entry[1]); return true;
            case "consoleDebug":
              if (debugConsoleRecord) debugConsoleRecord(entry[1], entry[2]);
              return true;
          }
          return false;
        } catch (e) { return false; }
      }
      (function drainObsvQueue() {
        try {
          var queued = window.__cwObsvQ;
          window.__cwObsvQ = { push: dispatchObsvQueueEntry };
          if (!Array.isArray(queued)) return;
          for (var i = 0; i < queued.length; i++) {
            var entry = queued[i];
            // The stub accepted this purchase before the runtime could validate
            // it, and its caller therefore did NOT publish. If the runtime now
            // rejects it, run the callback here or the event is lost outright.
            if (dispatchObsvQueueEntry(entry) === false &&
                Array.isArray(entry) && entry[0] === "purchase" && typeof entry[2] === "function") {
              try { entry[2](); } catch (e) {}
            }
          }
        } catch (e) {}
      })();

    } catch (e) {
      // observability must never break the page
    }
  })();
